import React, { useEffect, useMemo, useState } from "react";
import { hrApi } from "../../services/hrApi";

const ROLE_LIBRARY = [
  { key: "admin", name: "Admin", scope: "system" },
  { key: "ceo", name: "CEO", scope: "business" },
  { key: "cto", name: "CTO", scope: "business" },
  { key: "design_lead", name: "Design Lead", scope: "business" },
  { key: "design_manager", name: "Design Manager", scope: "business" },
  { key: "engineering_lead", name: "Engineering Lead", scope: "business" },
  { key: "finance_manager", name: "Finance Manager", scope: "business" },
  { key: "hr_admin", name: "HR Admin", scope: "module" },
  { key: "installation_manager", name: "Installation Manager", scope: "business" },
  { key: "marketing_manager", name: "Marketing Manager", scope: "business" },
  { key: "operations_manager", name: "Operations Manager", scope: "business" },
  { key: "product_owner", name: "Product Owner", scope: "business" },
  { key: "sales_manager", name: "Sales Manager", scope: "business" },
];

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium border ${
        active
          ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900"
          : "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-900 dark:text-gray-300 dark:border-neutral-700"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function RoleTag({ role, onDelete, deleting }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
      <span>{role.role_name || role.role_key}</span>
      <span className="text-xs opacity-70">({role.role_key})</span>
      <button
        type="button"
        onClick={() => onDelete(role)}
        disabled={deleting}
        className="text-xs opacity-80 hover:opacity-100"
        title="Remove role"
      >
        ✕
      </button>
    </div>
  );
}

function AddRoleModal({
  open,
  position,
  roles,
  onClose,
  onSubmit,
  saving,
}) {
  const [selectedRoleKey, setSelectedRoleKey] = useState("");
  const [customRoleKey, setCustomRoleKey] = useState("");
  const [customRoleName, setCustomRoleName] = useState("");
  const [roleScope, setRoleScope] = useState("business");
  const [allowCustom, setAllowCustom] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedRoleKey("");
    setCustomRoleKey("");
    setCustomRoleName("");
    setRoleScope("business");
    setAllowCustom(false);
  }, [open, position?.id]);

  const existingKeys = useMemo(
    () => new Set((roles || []).map((r) => String(r.role_key || "").toLowerCase())),
    [roles]
  );

  const availableRoles = useMemo(
    () =>
      ROLE_LIBRARY.filter((r) => !existingKeys.has(String(r.key).toLowerCase())),
    [existingKeys]
  );

  if (!open || !position) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (allowCustom) {
      if (!customRoleKey.trim() || !customRoleName.trim()) return;
      await onSubmit({
        position_id: position.id,
        role_key: customRoleKey.trim(),
        role_name: customRoleName.trim(),
        role_scope: roleScope,
        active_flag: 1,
      });
      return;
    }

    const selected = availableRoles.find((r) => r.key === selectedRoleKey);
    if (!selected) return;

    await onSubmit({
      position_id: position.id,
      role_key: selected.key,
      role_name: selected.name,
      role_scope: selected.scope,
      active_flag: 1,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl dark:bg-neutral-950 dark:text-white">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Add Position Role</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Position: {position.title} ({position.code || "-"})
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allowCustom}
              onChange={(e) => setAllowCustom(e.target.checked)}
            />
            Allow custom role
          </label>

          {!allowCustom ? (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Select role</label>
                <select
                  value={selectedRoleKey}
                  onChange={(e) => setSelectedRoleKey(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900"
                  required
                >
                  <option value="">Select role...</option>
                  {availableRoles.map((role) => (
                    <option key={role.key} value={role.key}>
                      {role.name} ({role.key})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Role key</label>
                <input
                  value={customRoleKey}
                  onChange={(e) => setCustomRoleKey(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900"
                  placeholder="design_manager"
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Role name</label>
                <input
                  value={customRoleName}
                  onChange={(e) => setCustomRoleName(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900"
                  placeholder="Design Manager"
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Scope</label>
                <select
                  value={roleScope}
                  onChange={(e) => setRoleScope(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900"
                >
                  <option value="system">System</option>
                  <option value="module">Module</option>
                  <option value="business">Business</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-black px-4 py-2 text-white disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ITPositionRolesPage() {
  const [positions, setPositions] = useState([]);
  const [rolesByPosition, setRolesByPosition] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(false);
  const [deletingRoleId, setDeletingRoleId] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(null);

  async function load() {
    try {
      setLoading(true);

      const res = await hrApi.getPositions({ limit: 500 });
      const items = res?.data?.data || res?.data?.items || [];

      setPositions(items);

      const roleEntries = await Promise.all(
        items.map(async (position) => {
          try {
            const roleRes = await hrApi.getPositionRoles(position.id);
            const roles = roleRes?.data?.data || roleRes?.data?.items || [];
            return [position.id, roles];
          } catch (err) {
            console.error("Failed to load position roles:", position.id, err);
            return [position.id, []];
          }
        })
      );

      setRolesByPosition(Object.fromEntries(roleEntries));
    } catch (err) {
      console.error("Failed to load position roles page:", err);
      alert(err?.response?.data?.message || "Failed to load position roles.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filteredPositions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return positions;

    return positions.filter((p) => {
      const roles = rolesByPosition[p.id] || [];
      const haystack = [
        p.title,
        p.code,
        p.department_name,
        p.team_name,
        ...roles.map((r) => r.role_key),
        ...roles.map((r) => r.role_name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [positions, rolesByPosition, query]);

  const totalRoles = useMemo(
    () =>
      Object.values(rolesByPosition).reduce((sum, roles) => sum + (roles?.length || 0), 0),
    [rolesByPosition]
  );

  async function handleCreateRole(payload) {
    try {
      setSavingRole(true);
      await hrApi.createPositionRole(payload);
      setSelectedPosition(null);
      await load();
    } catch (err) {
      console.error("Failed to create position role:", err);
      alert(err?.response?.data?.message || err.message || "Failed to create role.");
    } finally {
      setSavingRole(false);
    }
  }

  async function handleDeleteRole(role) {
    const confirmed = window.confirm(
      `Remove role "${role.role_name || role.role_key}" from this position?`
    );
    if (!confirmed) return;

    try {
      setDeletingRoleId(role.id);
      await hrApi.deletePositionRole(role.id);
      await load();
    } catch (err) {
      console.error("Failed to delete role:", err);
      alert(err?.response?.data?.message || err.message || "Failed to delete role.");
    } finally {
      setDeletingRoleId(null);
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Position Roles</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Assign access roles to organizational positions. Users inherit roles through active position assignments.
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          className="rounded-xl border px-4 py-2"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-2xl border p-4">
          <div className="text-sm text-gray-500">Positions</div>
          <div className="mt-2 text-3xl font-semibold">{positions.length}</div>
        </div>

        <div className="rounded-2xl border p-4">
          <div className="text-sm text-gray-500">Assigned roles</div>
          <div className="mt-2 text-3xl font-semibold">{totalRoles}</div>
        </div>

        <div className="rounded-2xl border p-4">
          <div className="text-sm text-gray-500">Positions with roles</div>
          <div className="mt-2 text-3xl font-semibold">
            {Object.values(rolesByPosition).filter((roles) => (roles?.length || 0) > 0).length}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border p-4">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by position, department, team or role..."
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-gray-50 dark:bg-neutral-900">
              <tr className="text-left">
                <th className="px-4 py-3">Position</th>
                <th className="px-4 py-3">Department / Team</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Roles</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-6" colSpan={5}>
                    Loading...
                  </td>
                </tr>
              ) : filteredPositions.length === 0 ? (
                <tr>
                  <td className="px-4 py-6" colSpan={5}>
                    No positions found.
                  </td>
                </tr>
              ) : (
                filteredPositions.map((position) => {
                  const roles = rolesByPosition[position.id] || [];

                  return (
                    <tr key={position.id} className="border-t border-gray-200 dark:border-neutral-800">
                      <td className="px-4 py-4 align-top">
                        <div className="font-medium">{position.title}</div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {position.code || "-"}
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div>{position.department_name || "-"}</div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {position.team_name || "-"}
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <StatusBadge active={Number(position.active_flag || 1) === 1} />
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-wrap gap-2">
                          {roles.length === 0 ? (
                            <span className="text-gray-500 dark:text-gray-400">No roles</span>
                          ) : (
                            roles.map((role) => (
                              <RoleTag
                                key={role.id}
                                role={role}
                                onDelete={handleDeleteRole}
                                deleting={deletingRoleId === role.id}
                              />
                            ))
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top text-right">
                        <button
                          type="button"
                          onClick={() => setSelectedPosition(position)}
                          className="rounded-xl bg-black px-4 py-2 text-white"
                        >
                          Add role
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddRoleModal
        open={!!selectedPosition}
        position={selectedPosition}
        roles={selectedPosition ? rolesByPosition[selectedPosition.id] || [] : []}
        onClose={() => setSelectedPosition(null)}
        onSubmit={handleCreateRole}
        saving={savingRole}
      />
    </div>
  );
}